import { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { AuthProvider } from "./hooks/useAuth";
import { TenantProvider, useTenant } from "./hooks/useTenant";
import { CartProvider } from "./hooks/useCart";
import { BrandProvider } from "./hooks/useBrand";
import { HomepageContentProvider } from "./hooks/useHomepageContent";
import { PlatformStatsProvider } from "./hooks/usePlatformStats";
import { useTranslationSync } from "./hooks/useTranslationSync";
import { CoreSettingsProvider } from "./hooks/useCoreSettings";
import { PwaProvider } from "./components/feature/PwaProvider";
import PwaEntryRedirect from "./components/feature/PwaEntryRedirect";
import PwaManifestSync from "./components/feature/PwaManifestSync";
import ErrorBoundary from "./components/feature/ErrorBoundary";
import PlatformErrorReporter from "./components/feature/PlatformErrorReporter";
import TenantSeoHead from "./components/feature/TenantSeoHead";
import GoogleAnalytics, { RouteChangeTracker } from "./components/feature/GoogleAnalytics";
import TenantStore from "./pages/tenant/TenantStore";
import TenantNotFound from "./pages/tenant/TenantNotFound";
import { loadAccountCapabilities } from "@/lib/accountCapabilities";

// Chemins qui doivent toujours ouvrir l'application (dashboard marchand /
// espace client), même lorsqu'on est sur le domaine personnalisé d'un marchand
// (ex: https://nomdedomaine.com/admin ou https://nomdedomaine.com/client).
// ⚠️ Les routes `/login-client` et `/register-client` sont VOLONTAIREMENT
// exclues : sur un sous-domaine / domaine de boutique, elles sont servies par
// le site public du tenant afin que le client reste SUR la marketplace.
const APP_PATH_RE = /^\/(admin|client|dashboard|superadmin|developpeur|mon-compte|mon-planning|abonnement|credit|mot-de-passe-oublie|reinitialiser-mot-de-passe|zcall|call|partage)(\/|$)/;

function AppShell() {
  const { isTenant, loading, unknownDomain } = useTenant();

  // Loading screen while resolving tenant
  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-sm text-foreground-500">Chargement...</p>
        </div>
      </div>
    );
  }

  const onAppPath =
    typeof window !== "undefined" &&
    APP_PATH_RE.test(window.location.pathname.replace(/\/+$/, "/"));

  // Domaine externe non associé à une boutique active → jamais de boutique au hasard
  if (unknownDomain && !onAppPath) {
    return <TenantNotFound />;
  }

  // Tenant store (subdomain or custom domain) — sauf si l'URL cible le dashboard
  if (isTenant && !onAppPath) {
    return <TenantStore />;
  }

  // Main Zifek site (zifek.fr) ou accès dashboard via domaine personnalisé
  return (
    <HomepageContentProvider>
      <PlatformStatsProvider>
        <BrandProvider>
          <CoreSettingsProvider>
            <BrowserRouter basename={__BASE_PATH__}>
              <RouteChangeTracker />
              <PwaEntryRedirect />
              <AppRoutes />
            </BrowserRouter>
          </CoreSettingsProvider>
        </BrandProvider>
      </PlatformStatsProvider>
    </HomepageContentProvider>
  );
}

function App() {
  useTranslationSync();
  // Charge les capacités des types de compte (table `typecompte`) une fois.
  useEffect(() => {
    void loadAccountCapabilities();
  }, []);
  return (
    <I18nextProvider i18n={i18n}>
      <PlatformErrorReporter />
      <GoogleAnalytics />
      <PwaManifestSync />
      <AuthProvider>
        <TenantProvider>
          <TenantSeoHead />
          <CartProvider>
            <PwaProvider>
              <ErrorBoundary>
                <AppShell />
              </ErrorBoundary>
            </PwaProvider>
          </CartProvider>
        </TenantProvider>
      </AuthProvider>
    </I18nextProvider>
  );
}

export default App;